import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Fade,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import GroupsIcon from '@mui/icons-material/Groups';
import PaidIcon from '@mui/icons-material/Paid';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SearchIcon from '@mui/icons-material/Search';
import TableChartIcon from '@mui/icons-material/TableChart';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis
} from 'recharts';

import reportService from '../services/reportService';

const COLORS = ['#4C7DFF', '#17B26A', '#F79009', '#F04438', '#7A5AF8', '#12B76A', '#0BA5EC'];

const REPORT_TYPES = [
  {
    value: 'projects',
    label: 'Projects',
    description: 'Budget, delivery status and project workload.',
    color: '#2F6FED',
    icon: FolderOpenIcon
  },
  {
    value: 'clients',
    label: 'Clients',
    description: 'Client status, revenue contribution and activity.',
    color: '#15803D',
    icon: GroupsIcon
  },
  {
    value: 'tasks',
    label: 'Tasks',
    description: 'Execution rhythm, priorities and overdue items.',
    color: '#D97706',
    icon: TaskAltIcon
  },
  {
    value: 'finances',
    label: 'Finances',
    description: 'Revenue, expenses, pending amounts and loans.',
    color: '#7C3AED',
    icon: PaidIcon
  },
  {
    value: 'users',
    label: 'Users',
    description: 'Team activity by workload and project coverage.',
    color: '#0F766E',
    icon: AutoGraphIcon
  }
];

const STATUS_OPTIONS_BY_REPORT = {
  projects: [
    { value: 'all', label: 'All statuses' },
    { value: 'planning', label: 'Planning' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'on_hold', label: 'On Hold' }
  ],
  clients: [
    { value: 'all', label: 'All statuses' },
    { value: 'active', label: 'Active' },
    { value: 'prospect', label: 'Prospect' },
    { value: 'inactive', label: 'Inactive' }
  ],
  tasks: [
    { value: 'all', label: 'All statuses' },
    { value: 'pending', label: 'Pending' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' }
  ],
  finances: [{ value: 'all', label: 'Not applicable' }],
  users: [{ value: 'all', label: 'Not applicable' }]
};

const DATE_PRESETS = [
  { value: 'last_30', label: 'Last 30 Days' },
  { value: 'quarter', label: 'This Quarter' },
  { value: 'year', label: 'This Year' },
  { value: 'clear', label: 'Clear Dates' }
];

const getCurrentDateInput = () => new Date().toISOString().split('T')[0];

const getShiftedDateInput = (days) => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString().split('T')[0];
};

const getQuarterStartInput = () => {
  const date = new Date();
  const quarterStartMonth = Math.floor(date.getMonth() / 3) * 3;
  return new Date(date.getFullYear(), quarterStartMonth, 1).toISOString().split('T')[0];
};

const getYearStartInput = () => {
  const date = new Date();
  return new Date(date.getFullYear(), 0, 1).toISOString().split('T')[0];
};

const getPresetDates = (preset) => {
  if (preset === 'last_30') {
    return { startDate: getShiftedDateInput(-30), endDate: getCurrentDateInput() };
  }

  if (preset === 'quarter') {
    return { startDate: getQuarterStartInput(), endDate: getCurrentDateInput() };
  }

  if (preset === 'year') {
    return { startDate: getYearStartInput(), endDate: getCurrentDateInput() };
  }

  return { startDate: '', endDate: '' };
};

const prettifyLabel = (value) => String(value || '')
  .replace(/_/g, ' ')
  .replace(/([a-z])([A-Z])/g, '$1 $2')
  .replace(/\b\w/g, (char) => char.toUpperCase());

const formatMetricValue = (key, value) => {
  if (value === null || value === undefined || value === '') return 'N/A';
  if (typeof value === 'string') return value;

  const normalizedKey = String(key || '').toLowerCase();

  if (normalizedKey.includes('margin')) return `${Number(value).toFixed(2)}%`;
  if (
    normalizedKey.includes('budget')
    || normalizedKey.includes('revenue')
    || normalizedKey.includes('income')
    || normalizedKey.includes('expense')
    || normalizedKey.includes('profit')
    || normalizedKey.includes('pending')
    || normalizedKey.includes('loan')
    || normalizedKey.includes('net')
  ) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 2
    }).format(Number(value) || 0);
  }

  if (typeof value === 'number') return value.toLocaleString('en-US');
  return value;
};

const formatTableValue = (key, value) => {
  if (value === null || value === undefined || value === '') return '-';

  const normalizedKey = String(key || '').toLowerCase();

  if ((normalizedKey.includes('date') || normalizedKey.endsWith('_at')) && value) {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' });
    }
  }

  if (typeof value === 'number') {
    if (
      normalizedKey.includes('budget')
      || normalizedKey.includes('revenue')
      || normalizedKey.includes('amount')
      || normalizedKey.includes('expense')
      || normalizedKey.includes('profit')
      || normalizedKey.includes('pending')
      || normalizedKey.includes('quotes')
      || normalizedKey.includes('loan')
      || normalizedKey.includes('net')
      || normalizedKey.includes('purchase_orders')
    ) {
      return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    return value.toLocaleString('en-US');
  }

  return value;
};

const buildActiveFilterChips = ({ reportType, status, startDate, endDate, rowsCount }) => {
  const chips = [{ key: 'type', label: `Report: ${prettifyLabel(reportType)}` }];

  if (status && status !== 'all') chips.push({ key: 'status', label: `Status: ${prettifyLabel(status)}` });
  if (startDate) chips.push({ key: 'start', label: `From: ${startDate}` });
  if (endDate) chips.push({ key: 'end', label: `To: ${endDate}` });
  if (Number.isFinite(rowsCount)) chips.push({ key: 'rows', label: `${rowsCount} rows` });

  return chips;
};

const EmptyState = ({ color }) => (
  <Paper
    elevation={0}
    sx={{
      p: 5,
      borderRadius: 4,
      border: '1px dashed',
      borderColor: alpha(color, 0.35),
      bgcolor: alpha(color, 0.05),
      textAlign: 'center'
    }}
  >
    <AssessmentIcon sx={{ fontSize: 56, color }} />
    <Typography variant="h6" sx={{ mt: 2, fontWeight: 800 }}>
      Generate a report to explore your data
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 560, mx: 'auto' }}>
      Pick a report type, apply a date range or status if needed, and generate a view ready for export.
    </Typography>
  </Paper>
);

const StatCard = ({ title, value, color }) => (
  <Card
    elevation={0}
    sx={{
      height: '100%',
      borderRadius: 3,
      border: '1px solid',
      borderColor: alpha(color, 0.24),
      backgroundImage: `linear-gradient(135deg, ${alpha(color, 0.16)} 0%, ${alpha('#ffffff', 0.02)} 100%)`,
      boxShadow: `0 18px 36px ${alpha(color, 0.08)}`
    }}
  >
    <CardContent sx={{ p: 2.4 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.7, fontWeight: 700, letterSpacing: '0.04em' }}>
        {prettifyLabel(title)}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 800, color }}>
        {value}
      </Typography>
    </CardContent>
  </Card>
);

export default function ReportGenerator() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [reportType, setReportType] = useState('projects');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [status, setStatus] = useState('all');
  const [tableSearch, setTableSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [lastGeneratedAt, setLastGeneratedAt] = useState('');

  const currentReportConfig = useMemo(
    () => REPORT_TYPES.find((entry) => entry.value === reportType) || REPORT_TYPES[0],
    [reportType]
  );

  const statusOptions = STATUS_OPTIONS_BY_REPORT[reportType] || STATUS_OPTIONS_BY_REPORT.projects;
  const statusEnabled = statusOptions.length > 1;

  const summaryEntries = useMemo(
    () => Object.entries(reportData?.summary || {}),
    [reportData]
  );

  const chartKeys = useMemo(() => {
    if (!Array.isArray(reportData?.chartData) || !reportData.chartData.length) return [];
    return Object.keys(reportData.chartData[0]).filter((key) => key !== 'name');
  }, [reportData]);

  const filteredTableData = useMemo(() => {
    const rows = Array.isArray(reportData?.tableData) ? reportData.tableData : [];
    const search = tableSearch.trim().toLowerCase();
    if (!search) return rows;

    return rows.filter((row) => Object.values(row).some((value) => String(value ?? '').toLowerCase().includes(search)));
  }, [reportData, tableSearch]);

  const activeFilterChips = useMemo(
    () => buildActiveFilterChips({
      reportType,
      status,
      startDate,
      endDate,
      rowsCount: Array.isArray(reportData?.tableData) ? reportData.tableData.length : undefined
    }),
    [endDate, reportData, reportType, startDate, status]
  );

  const handleGenerateReport = useCallback(async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const filters = {
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        status: statusEnabled && status !== 'all' ? status : undefined
      };

      let data;
      if (reportType === 'projects') data = await reportService.getProjectsReport(filters);
      else if (reportType === 'clients') data = await reportService.getClientsReport(filters);
      else if (reportType === 'tasks') data = await reportService.getTasksReport(filters);
      else if (reportType === 'finances') data = await reportService.getFinancesReport(filters);
      else data = await reportService.getUsersReport();

      setReportData(data);
      setLastGeneratedAt(new Date().toLocaleString('en-US'));
      setSuccess('Report generated successfully.');
    } catch (err) {
      setReportData(null);
      setError(`Error generating report: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoading(false);
    }
  }, [endDate, reportType, startDate, status, statusEnabled]);

  const handleExportPDF = useCallback(async () => {
    if (!reportData) return;
    try {
      setSuccess('Generating PDF...');
      await reportService.exportToPDF(reportType, reportData);
      setSuccess('PDF exported successfully.');
    } catch (err) {
      setError(`Error exporting PDF: ${err.message}`);
    }
  }, [reportData, reportType]);

  const handleExportExcel = useCallback(async () => {
    if (!reportData) return;
    try {
      setSuccess('Generating Excel...');
      await reportService.exportToExcel(reportType, reportData);
      setSuccess('Excel exported successfully.');
    } catch (err) {
      setError(`Error exporting Excel: ${err.message}`);
    }
  }, [reportData, reportType]);

  const handleApplyDatePreset = useCallback((preset) => {
    const nextRange = getPresetDates(preset);
    setStartDate(nextRange.startDate);
    setEndDate(nextRange.endDate);
  }, []);

  const handleResetFilters = useCallback(() => {
    setStartDate('');
    setEndDate('');
    setStatus('all');
    setTableSearch('');
    setReportData(null);
    setSuccess('');
    setError('');
  }, []);

  const handleSelectReportType = useCallback((type) => {
    setReportType(type);
    setStatus('all');
    setTableSearch('');
    setReportData(null);
    setSuccess('');
    setError('');
  }, []);

  return (
    <Box sx={{ width: '100%' }}>
      <Fade in timeout={500}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.4, md: 1.7 },
            mb: 1.5,
            borderRadius: 2.5,
            color: 'white',
            overflow: 'hidden',
            position: 'relative',
            border: '1px solid',
            borderColor: alpha(currentReportConfig.color, 0.3),
            backgroundImage: `
              radial-gradient(circle at top right, ${alpha('#ffffff', 0.16)} 0%, transparent 28%),
              linear-gradient(135deg, ${currentReportConfig.color} 0%, ${alpha(currentReportConfig.color, 0.78)} 100%)
            `,
            boxShadow: `0 10px 24px ${alpha(currentReportConfig.color, 0.18)}`
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Box sx={{ p: 1, borderRadius: 2, bgcolor: alpha('#ffffff', 0.18), display: 'flex' }}>
                <AssessmentIcon sx={{ fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
                  Reports Center
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.92, maxWidth: 640 }}>
                  Generate cleaner operational snapshots with real filters, exports and a sharper data story.
                </Typography>
              </Box>
            </Box>

            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              <Chip label={currentReportConfig.label} sx={{ bgcolor: alpha('#ffffff', 0.18), color: 'white', fontWeight: 700 }} />
              {lastGeneratedAt && (
                <Chip label={`Updated ${lastGeneratedAt}`} sx={{ bgcolor: alpha('#ffffff', 0.12), color: 'white' }} />
              )}
            </Stack>
          </Box>
        </Paper>
      </Fade>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2.5 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 2.5 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Grid container spacing={1.2} sx={{ mb: 1.5 }}>
        {REPORT_TYPES.map((type) => {
          const Icon = type.icon;
          const isActive = type.value === reportType;
          return (
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              key={type.value}
              sx={{
                flexBasis: { lg: '20%' },
                maxWidth: { lg: '20%' }
              }}
            >
              <Card
                onClick={() => handleSelectReportType(type.value)}
                sx={{
                  height: '100%',
                  cursor: 'pointer',
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: isActive ? alpha(type.color, 0.5) : 'divider',
                  bgcolor: isActive ? alpha(type.color, 0.08) : alpha('#ffffff', 0.02),
                  boxShadow: isActive ? `0 18px 36px ${alpha(type.color, 0.12)}` : 'none',
                  transition: 'all 180ms ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    borderColor: alpha(type.color, 0.45),
                    boxShadow: `0 18px 36px ${alpha(type.color, 0.12)}`
                  }
                }}
              >
                <CardContent sx={{ p: 2.2 }}>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.2 }}>
                    <Box
                      sx={{
                        width: 42,
                        height: 42,
                        borderRadius: 2.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: alpha(type.color, 0.14),
                        color: type.color
                      }}
                    >
                      <Icon fontSize="small" />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                        {type.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {isActive ? 'Selected' : 'Click to switch'}
                      </Typography>
                    </Box>
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {type.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          mb: 1.5,
          borderRadius: 2.5,
          border: '1px solid',
          borderColor: alpha(currentReportConfig.color, 0.2)
        }}
      >
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', lg: 'center' }} justifyContent="space-between">
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Configure Report
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Use date presets, status filters and exports without leaving the page.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {DATE_PRESETS.map((preset) => (
              <Chip
                key={preset.value}
                label={preset.label}
                clickable
                onClick={() => handleApplyDatePreset(preset.value)}
                variant="outlined"
              />
            ))}
          </Stack>
        </Stack>

        <Grid container spacing={1.2} sx={{ mt: 0.25 }}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarMonthIcon fontSize="small" />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="End Date"
              type="date"
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarMonthIcon fontSize="small" />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={status}
                onChange={(event) => setStatus(event.target.value)}
                disabled={!statusEnabled}
              >
                {statusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ height: '100%' }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<RestartAltIcon />}
                onClick={handleResetFilters}
                sx={{ borderRadius: 2.5, fontWeight: 700 }}
              >
                Reset
              </Button>
              <Button
                fullWidth
                variant="contained"
                startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <VisibilityIcon />}
                onClick={handleGenerateReport}
                disabled={loading}
                sx={{
                  borderRadius: 2.5,
                  fontWeight: 700,
                  boxShadow: `0 12px 26px ${alpha(currentReportConfig.color, 0.25)}`,
                  bgcolor: currentReportConfig.color,
                  '&:hover': { bgcolor: currentReportConfig.color }
                }}
              >
                {loading ? 'Generating...' : 'Generate'}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {!reportData && !loading && <EmptyState color={currentReportConfig.color} />}

      {reportData && (
        <Fade in timeout={450}>
          <Box>
            <Paper
              elevation={0}
              sx={{
                p: 2.2,
                mb: 3,
                borderRadius: 3,
                border: '1px solid',
                borderColor: alpha(currentReportConfig.color, 0.2),
                bgcolor: alpha(currentReportConfig.color, 0.05)
              }}
            >
              <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', lg: 'center' }} justifyContent="space-between">
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {activeFilterChips.map((chip) => (
                    <Chip key={chip.key} label={chip.label} size="small" />
                  ))}
                </Stack>

                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  <Tooltip title="Regenerate">
                    <IconButton onClick={handleGenerateReport} disabled={loading}>
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                  <Button variant="outlined" color="error" startIcon={<PictureAsPdfIcon />} onClick={handleExportPDF}>
                    PDF
                  </Button>
                  <Button variant="outlined" color="success" startIcon={<TableChartIcon />} onClick={handleExportExcel}>
                    Excel
                  </Button>
                </Stack>
              </Stack>
            </Paper>

            {summaryEntries.length > 0 && (
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {summaryEntries.map(([key, value], index) => (
                  <Grid item xs={12} sm={6} md={3} key={key}>
                    <StatCard
                      title={key}
                      value={formatMetricValue(key, value)}
                      color={COLORS[index % COLORS.length]}
                    />
                  </Grid>
                ))}
              </Grid>
            )}

            <Grid container spacing={3} sx={{ mb: 3 }}>
              {Array.isArray(reportData.chartData) && reportData.chartData.length > 0 && chartKeys.length > 0 && (
                <Grid item xs={12} lg={reportData.pieData?.length ? 8 : 12}>
                  <Paper elevation={0} sx={{ p: 3, borderRadius: 3.5, border: '1px solid', borderColor: 'divider', height: '100%' }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                      <TrendingUpIcon sx={{ color: currentReportConfig.color }} />
                      <Typography variant="h6" sx={{ fontWeight: 800 }}>
                        Trend View
                      </Typography>
                    </Stack>
                    <ResponsiveContainer width="100%" height={isMobile ? 280 : 380}>
                      <BarChart data={reportData.chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke={alpha('#000000', 0.08)} />
                        <XAxis dataKey="name" tickLine={false} axisLine={false} />
                        <YAxis tickLine={false} axisLine={false} />
                        <RechartsTooltip
                          contentStyle={{
                            borderRadius: 12,
                            border: `1px solid ${alpha(currentReportConfig.color, 0.18)}`,
                            boxShadow: `0 16px 32px ${alpha(currentReportConfig.color, 0.12)}`
                          }}
                        />
                        <Legend />
                        {chartKeys.map((key, index) => (
                          <Bar
                            key={key}
                            dataKey={key}
                            fill={COLORS[index % COLORS.length]}
                            radius={[8, 8, 0, 0]}
                          />
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  </Paper>
                </Grid>
              )}

              {Array.isArray(reportData.pieData) && reportData.pieData.length > 0 && (
                <Grid item xs={12} lg={reportData.chartData?.length ? 4 : 12}>
                  <Paper elevation={0} sx={{ p: 3, borderRadius: 3.5, border: '1px solid', borderColor: 'divider', height: '100%' }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                      <AssessmentIcon sx={{ color: currentReportConfig.color }} />
                      <Typography variant="h6" sx={{ fontWeight: 800 }}>
                        Distribution
                      </Typography>
                    </Stack>
                    <ResponsiveContainer width="100%" height={isMobile ? 280 : 380}>
                      <PieChart>
                        <Pie
                          data={reportData.pieData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={isMobile ? 82 : 120}
                          label={isMobile ? false : ({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        >
                          {reportData.pieData.map((entry, index) => (
                            <Cell key={`${entry.name}-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Legend />
                        <RechartsTooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </Paper>
                </Grid>
              )}
            </Grid>

            <Paper elevation={0} sx={{ borderRadius: 3.5, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
              <Box sx={{ p: 2.5, bgcolor: alpha(currentReportConfig.color, 0.05) }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between">
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>
                      Report Details
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Search within the generated rows before exporting.
                    </Typography>
                  </Box>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} alignItems={{ xs: 'stretch', sm: 'center' }}>
                    <TextField
                      size="small"
                      value={tableSearch}
                      onChange={(event) => setTableSearch(event.target.value)}
                      placeholder="Search rows..."
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        )
                      }}
                    />
                    <Chip label={`${filteredTableData.length} visible rows`} color="primary" variant="outlined" />
                  </Stack>
                </Stack>
              </Box>

              <Divider />

              {filteredTableData.length > 0 ? (
                <>
                <Stack spacing={1.2} sx={{ display: { xs: 'flex', md: 'none' }, p: 1.3 }}>
                  {filteredTableData.map((row, rowIndex) => (
                    <Paper key={`mobile-row-${rowIndex}`} variant="outlined" sx={{ p: 1.4, borderRadius: 2.2 }}>
                      <Stack spacing={0.9}>
                        {Object.entries(row).map(([key, value]) => (
                          <Box key={key} sx={{ display: 'grid', gridTemplateColumns: 'minmax(92px, 0.8fr) minmax(0, 1.4fr)', gap: 1, alignItems: 'start' }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={800} sx={{ textTransform: 'uppercase' }}>
                              {prettifyLabel(key)}
                            </Typography>
                            <Typography variant="body2" sx={{ textAlign: 'right', overflowWrap: 'anywhere' }}>
                              {formatTableValue(key, value)}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
                <TableContainer sx={{ display: { xs: 'none', md: 'block' }, maxHeight: 620 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        {Object.keys(filteredTableData[0]).map((key) => (
                          <TableCell
                            key={key}
                            sx={{
                              fontWeight: 800,
                              textTransform: 'uppercase',
                              letterSpacing: '0.04em',
                              bgcolor: currentReportConfig.color,
                              color: 'white',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {prettifyLabel(key)}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredTableData.map((row, rowIndex) => (
                        <TableRow
                          key={`row-${rowIndex}`}
                          hover
                          sx={{
                            '&:nth-of-type(even)': { bgcolor: alpha('#000000', 0.015) },
                            '&:hover': { bgcolor: alpha(currentReportConfig.color, 0.05) }
                          }}
                        >
                          {Object.entries(row).map(([key, value], cellIndex) => (
                            <TableCell key={`${key}-${cellIndex}`}>
                              {formatTableValue(key, value)}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                </>
              ) : (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="body1" sx={{ fontWeight: 700 }}>
                    No rows match the current filters
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.8 }}>
                    Try changing the search text or regenerate the report with broader filters.
                  </Typography>
                </Box>
              )}
            </Paper>
          </Box>
        </Fade>
      )}
    </Box>
  );
}
